const OPEN_DIALOG_SELECTOR = '[role="dialog"], [role="alertdialog"]'

interface FocusAfterDialogsCloseOptions {
  /** Used when `getTarget` still finds nothing after `maxFrames` frames. */
  fallback?: () => HTMLElement | null | undefined
  maxFrames?: number
}

/**
 * Focuses the element returned by `getTarget` once no dialog is open.
 *
 * A closing dialog hands focus back to the control that opened it when its
 * exit animation ends. When that control was removed in the meantime (e.g. a
 * deleted row), focus would fall to the page, so wait until the dialog is gone.
 * Retries for a few frames while the target isn't rendered yet (virtualized
 * lists render rows after measuring).
 *
 * @returns a cleanup function that cancels the pending focus.
 */
export const focusAfterDialogsClose = (
  getTarget: () => HTMLElement | null | undefined,
  { fallback, maxFrames = 30 }: FocusAfterDialogsCloseOptions = {},
) => {
  let frame = 0
  let framesWithoutTarget = 0
  const attempt = () => {
    if (document.querySelector(OPEN_DIALOG_SELECTOR)) {
      frame = requestAnimationFrame(attempt)
      return
    }
    const target = getTarget()
    if (target) {
      target.focus()
    } else if (framesWithoutTarget++ < maxFrames) {
      frame = requestAnimationFrame(attempt)
    } else {
      fallback?.()?.focus()
    }
  }
  frame = requestAnimationFrame(attempt)
  return () => cancelAnimationFrame(frame)
}
